import { useUnit } from 'effector-react';
import { useNavigateControlbar } from '@nx-frontend/shared/hooks/use-navigate';
import { useChanges } from '@nx-frontend/shared/hooks/useChangeEvents';
import { generateLinkMenuItem, getOrderByIdFx } from '@nx-frontend/shared/store/feature-menu-tree';
import { $virtualNodePayload, VirtualNodePayload } from '@nx-frontend/shared/store/virtual-portal-node';
import { ChangeEventType, ControlbarEditEntityType, LinkMenuItem } from '@nx-frontend/shared/types';

interface LinkCreatePayload {
  title: string;
  url: string;
  openInNewTab: boolean;
  visible?: boolean;
  openAfterCreate?: boolean;
}

export const useLinkCreate = (): ((
  payload: LinkCreatePayload,
  pagePayload?: VirtualNodePayload
) => Promise<{ menuItem: LinkMenuItem }>) => {
  const [virtualPagePayload, getOrderById] = useUnit([$virtualNodePayload, getOrderByIdFx]);
  const setChanges = useChanges();
  const navigateControlbar = useNavigateControlbar();

  return async (
    { title, url, openInNewTab, visible, openAfterCreate }: LinkCreatePayload,
    pagePayload?: VirtualNodePayload
  ) => {
    if (!pagePayload && !virtualPagePayload) {
      throw new Error('Missing node payload');
    }

    const menuType = pagePayload?.menuType ?? virtualPagePayload?.menuType;
    if (!menuType) {
      throw new Error('Missing node menu type');
    }

    const parentId = pagePayload?.parentId ?? virtualPagePayload?.parentId ?? '';

    const parentOrder = await getOrderById({ menuType, id: parentId });
    const linkMenuItem = generateLinkMenuItem({
      name: title,
      url,
      parentId,
      openInNewTab,
      index: parentOrder ? parentOrder?.length : 0,
      visible,
    });
    setChanges({
      type: ChangeEventType.ADD_MENU_ITEM,
      oldValue: undefined,
      newValue: { menuType, item: linkMenuItem },
    });

    if (openAfterCreate) {
      navigateControlbar({ entityType: ControlbarEditEntityType.LINK, entityId: linkMenuItem.id });
    }

    return {
      menuItem: linkMenuItem,
    };
  };
};
